/**
 * User Domain - Data Transformations
 * Hanterar transformering av användardata för API-anrop
 */

/**
 * Beräknar companyName baserat på roll
 * @param {string} role - Användarens roll
 * @param {string} currentCompanyName - Nuvarande företagsnamn
 * @returns {string} Företagsnamn
 */
export const getCompanyNameForRole = (role, currentCompanyName = '') => {
  if (role === 'byggkonsult' || role === 'inredningsarkitekt') {
    return 'Leadme AB';
  }
  
  if (role === 'UE' || role === 'projektor' || role === 'materialleverantör') {
    return currentCompanyName;
  }

  // För kund, byggare, admin
  return '';
};

/**
 * Förbereder användardata för backend (create)
 * Rensar bort frontend-only fält och lägger till rollspecifika fält
 * @param {Object} values - Formulärets värden
 * @returns {Object} Data redo för backend
 */
export const prepareUserDataForCreate = (values) => {
  const userData = { ...values };
  
  // Ta bort frontend-only fält
  delete userData.selectedBuilder;
  delete userData.selectedSupplier;
  delete userData.sendWelcomeEmail; // Frontend-only flag
  
  // Lägg till rätt data baserat på roll
  if (userData.role === 'byggare') {
    userData.builder = values.selectedBuilder;
  }
  
  if (userData.role === 'materialleverantör') {
    userData.supplier = values.selectedSupplier;
  }
  
  if (userData.role === 'byggkonsult' || userData.role === 'inredningsarkitekt') {
    userData.companyName = 'Leadme AB';
  }
  
  if (userData.role === 'UE') {
    userData.contractorType = values.contractorType;
  }

  if (userData.role === 'projektor') {
    userData.projectorType = values.projectorType;
  }

  return userData;
};

/**
 * Förbereder användardata för backend (update)
 * @param {Object} values - Formulärets värden
 * @param {Object} originalUser - Original användarobjekt (för att jämföra ändringar)
 * @returns {Object} Data redo för backend
 */
export const prepareUserDataForUpdate = (values, originalUser) => {
  const userData = { ...values };
  
  // Sätt companyName baserat på roll
  if (userData.role === 'byggkonsult' || userData.role === 'inredningsarkitekt') {
    userData.companyName = 'Leadme AB';
  } else if (userData.role === 'kund' || userData.role === 'admin' || userData.role === 'byggare') {
    userData.companyName = '';
    userData.contractorType = '';
    userData.projectorType = '';
  }
  
  // Om användaren ändras från byggare, rensa builder
  if (originalUser.role === 'byggare' && userData.role !== 'byggare') {
    userData.selectedBuilder = '';
  }
  
  // Om användaren ändras från materialleverantör, rensa supplier
  if (originalUser.role === 'materialleverantör' && userData.role !== 'materialleverantör') {
    userData.selectedSupplier = '';
  }
  
  // Hantera supplier för materialleverantörer
  if (userData.role === 'materialleverantör') {
    userData.supplier = userData.selectedSupplier;
  }
  
  // Hantera builder för byggare
  if (userData.role === 'byggare') {
    userData.builder = userData.selectedBuilder;
  }
  
  // Ta bort onödiga fält
  delete userData.selectedBuilder;
  delete userData.selectedSupplier;
  
  // Hantera requirePasswordChange
  if (!values.requirePasswordChange) {
    delete userData.requirePasswordChange;
  }
  
  return userData;
};

/**
 * Konverterar användardata till FormData om det finns fil eller removeAvatar
 * @param {Object} userData - Användardata
 * @returns {Object} { body, isFormData }
 */
export const convertToFormDataIfNeeded = (userData) => {
  const hasFile = userData.avatar instanceof File;
  const hasRemoveFlag = userData.removeAvatar === true;
  
  if (!hasFile && !hasRemoveFlag) {
    return {
      body: userData,
      isFormData: false
    };
  }
  
  const formData = new FormData();
  
  Object.entries(userData).forEach(([key, value]) => {
    if (key === 'avatar' && value instanceof File) {
      formData.append('avatar', value);
    } else if (key === 'removeAvatar' && value) {
      formData.append('removeAvatar', true);
    } else if (typeof value === 'boolean') {
      // Booleans måste skickas som strängar som backend kan parsa
      formData.append(key, value ? 'true' : 'false');
    } else if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof File)) {
      // Platta nästlade objekt till dot-notation (t.ex. marketingTabAccess.fotografering)
      Object.entries(value).forEach(([nestedKey, nestedVal]) => {
        if (typeof nestedVal === 'boolean') {
          formData.append(`${key}.${nestedKey}`, nestedVal ? 'true' : 'false');
        } else if (nestedVal !== undefined && nestedVal !== null) {
          formData.append(`${key}.${nestedKey}`, nestedVal);
        }
      });
    } else {
      formData.append(key, value);
    }
  });
  
  return {
    body: formData,
    isFormData: true
  };
};

/**
 * Validerar och förbereder avatar-fil
 * @param {File} file - Avatar-filen
 * @returns {Object} { isValid, error, file }
 */
export const validateAvatarFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'Ingen fil vald', file: null };
  }
  
  if (!file.type.startsWith('image/')) {
    return { isValid: false, error: 'Endast bildfiler är tillåtna', file: null };
  }
  
  const maxSize = 1024 * 1024; // 1MB
  if (file.size > maxSize) {
    return { isValid: false, error: 'Bilden får inte vara större än 1MB', file: null };
  }
  
  return { isValid: true, error: null, file };
};

